/**
 * How the settings store reaches localStorage — the one genuinely subtle thing about
 * persisting this state.
 *
 * `Infinity` IS A LEGAL REPEAT VALUE AND IS NOT JSON-REPRESENTABLE (A20).
 * `JSON.stringify(Infinity)` is `"null"`, so a persisted "repeat forever" would
 * rehydrate as `null` and the reducer's `arm()` arithmetic would go `NaN`. The codec
 * below maps it to a sentinel in both directions, which is what makes persisting
 * repeat counts safe at all.
 *
 * THIS IS ALSO WHY THE THREE SLICES SHARE ONE KEY: the codec is a property of the
 * whole payload, and three stores would mean three copies of it to keep in step.
 */
import { createJSONStorage } from 'zustand/middleware'
import type { SettingsState } from './types'

/**
 * A sentinel STRING, not a magic number: 0 and -1 are both plausible repeat counts to
 * a future reader, and a number sentinel would survive a schema change silently. A
 * string cannot be mistaken for a count, and a stale one decodes to a safe finite 1.
 */
const INF = '__Infinity__'

export const settingsStorage = createJSONStorage<Partial<SettingsState>>(() => localStorage, {
  replacer: (_key, value) => (value === Infinity ? INF : value),
  reviver: (_key, value) => (value === INF ? Infinity : value),
})

/**
 * `audioEpoch` is session state and the actions are functions — neither belongs in
 * localStorage. Listing the fields explicitly (rather than deleting keys) means a new
 * *action* can never accidentally be persisted, and adding a field to a slice without
 * adding it here fails loudly on the next reload rather than quietly.
 */
export const partializeSettings = (s: SettingsState): Partial<SettingsState> => ({
  // display
  theme: s.theme,
  mode: s.mode,
  fontScale: s.fontScale,
  // audio
  reciterId: s.reciterId,
  playbackRate: s.playbackRate,
  followRecitation: s.followRecitation,
  autoAdvancePage: s.autoAdvancePage,
  // repeat
  repeatAyah: s.repeatAyah,
  repeatPassage: s.repeatPassage,
  repeatBar: s.repeatBar,
  // english
  translation: s.translation,
  translit: s.translit,
  englishScale: s.englishScale,
  panelExpanded: s.panelExpanded,
  stripLine: s.stripLine,
})
